import { ApiClientError } from './client'

/**
 * Which sentence tells an admin that a write was refused, as a catalogue key:
 * never the API's English `message` (CLAUDE.md rule 9, docs/LOCALE.md §3.5).
 *
 * The policy the owner chose on 2026-09-30 for the library composers (landing
 * page, email template, phishing page):
 *   1. An `errorCode` the screen knows reads its own sentence (`codeKeys`).
 *   2. Otherwise the HTTP status picks one (REFUSAL_KEY_BY_STATUS).
 *   3. Anything else reads the generic sentence: a 5xx, a network failure, or
 *      a throw that is not an API refusal at all.
 *
 * What the API actually said is not lost; it goes to the console through
 * logRefusal(). Until then the composers either printed it on screen (#364 /
 * #366 / #370-F: "surface the backend validation message[], e.g. DTO-drift
 * 400s"), so an Arabic admin read "property htmlContent should not exist"
 * (PROD-3), or replaced every refusal with one generic sentence (71d3e97a5),
 * which hid the drift #370-F wanted visible.
 */
export const GENERIC_REFUSAL_KEY = 'errors.generic'

/**
 * The sentence for a refusal whose errorCode (if any) the screen does not know.
 * 409 is left out on purpose: the catalogue has no general conflict sentence,
 * so a conflict reads the generic one.
 */
export const REFUSAL_KEY_BY_STATUS: Readonly<Record<number, string>> = {
  // The validation pipe (a DTO drift among them) or a hand-thrown BadRequest.
  400: 'errors.invalidFields',
  403: 'errors.unauthorized',
  404: 'errors.notFound',
}

/**
 * Codes any screen may receive, whatever it maps itself: the impersonation
 * refusal (403 { code: 'BLOCKED_DURING_IMPERSONATION' }) read as the generic
 * "not authorized" everywhere (review of the security fixes, 25 Oct 2026).
 */
export const REFUSAL_KEY_BY_SHARED_CODE: ReadonlyMap<string, string> = new Map([
  ['BLOCKED_DURING_IMPERSONATION', 'errors.blockedDuringImpersonation'],
])

/**
 * The catalogue key to show for `error`. `codeKeys` maps the errorCodes this
 * screen knows (the API's `errorCode`, read by ApiClientError) to their keys;
 * a screen's own key wins over a shared one.
 */
export function refusalKey(
  error: unknown,
  codeKeys: ReadonlyMap<string, string> = new Map(),
): string {
  if (!(error instanceof ApiClientError)) return GENERIC_REFUSAL_KEY
  const byCode = error.errorCode
    ? codeKeys.get(error.errorCode) ?? REFUSAL_KEY_BY_SHARED_CODE.get(error.errorCode)
    : undefined
  if (byCode) return byCode
  return REFUSAL_KEY_BY_STATUS[error.statusCode] ?? GENERIC_REFUSAL_KEY
}

/**
 * Write what the API said to the browser console, for support. This is the
 * only place it appears: the admin reads refusalKey()'s sentence. The body is
 * logged whole, so a validation pipe's message[] (a DTO drift) and a 5xx's
 * `traceId` (which ties it to the server journal) both reach whoever opens the
 * console.
 */
export function logRefusal(what: string, error: unknown): void {
  console.error(
    `${what} was refused`,
    error instanceof ApiClientError
      ? { status: error.statusCode, errorCode: error.errorCode, body: error.body }
      : error,
  )
}
